/* eslint-disable @typescript-eslint/no-dynamic-delete */

type Value = Record<string, any>;

/**
 * 模块说明
 * -------
 * 对象工具
 */
export default class Obj {
    private readonly value: Record<string, any>;

    constructor(value: Record<string, any>) {
        this.value = value;
    }

    // 取值 (支持深度取值，如a.b.c)
    get = (key: string) => {
        if (!this.value) return null;
        return key.split('.').reduce((s, k: string) => (s ? s[k] : null), this.value);
    };

    // 赋值
    set = (key: string, value: any): Obj => {
        if (!key || !this.value || typeof this.value !== 'object') return this;
        const ks = key.split('.');
        let v = this.value;
        for (const k of ks.slice(0, ks.length - 1)) {
            const kv = v[k];
            if (kv === undefined || kv === null) {
                v[k] = {};
                v = {};
            } else if (typeof v === 'object') {
                v = kv;
            } else {
                return this;
            }
        }
        const k = ks[ks.length - 1];
        if (value === undefined) {
            delete v[k];
        } else {
            v[k] = value;
        }
        return this;
    };

    // 字段适配转换 (键名转换/抖动去值)
    adapt = (mapping: Array<[string, string] | string>, shake?: boolean): any => {
        if (!this.value) return this.value;
        return mapping
            ? mapping.reduce(
                  (adaption, item) => {
                      const [key, target] = item instanceof Array ? item : [item, item];
                      return {
                          ...adaption,
                          [key]: undefined,
                          [target]: this.value[key],
                      };
                  },
                  shake ? {} : this.value,
              )
            : null;
    };

    // 匹配模型 (key匹配)
    match = (model: Record<string, any>): Value => {
        if (!model || !this.value) return this.value;
        const keys = Object.keys(model);
        return Object.entries(this.value).reduce((s, [k, v]) => {
            if (keys.includes(k)) return { ...s, [k]: v };
            else return s;
        }, {});
    };

    // 校验是否包含指定key且value为真
    includes = (keys: string[]): boolean => {
        if (!this.value) return false;
        for (let i = 0; i < keys.length; i++) {
            if (!this.get(keys[i])) return false;
        }
        return true;
    };

    // 字段移除
    eliminate = (fields: string[]): Value => {
        if (!this.value) return this.value;
        return fields ? this.extract(Object.keys(this.value).filter((key) => !fields.includes(key))) : this.value;
    };

    // 字段提取
    extract = (fields: string[]): Record<string, any> => {
        if (!this.value) return this.value;
        return fields
            ? fields.reduce((s, k) => {
                  const v = this.value[k];
                  return ['number', 'boolean'].includes(typeof v) || v ? { ...s, [k]: v } : s;
              }, {})
            : null;
    };

    /**
     * 驼峰转下划线
     * @param isReplace 是否覆盖原参数
     */
    toLine = (isReplace: boolean = false): Value => {
        if (!this.value) return this;
        Object.keys(this.value).forEach((k) => {
            this.value[k.replace(/([A-Z])/g, '_$1').toLowerCase()] = this.value[k];
            if (isReplace && k.search(/([A-Z])/) >= 0) delete this.value[k];
        });
        return this;
    };

    /**
     * 下划线转驼峰
     * @param isReplace 是否覆盖原参数
     */
    toCamelCace = (isReplace: boolean = false): Value => {
        if (!this.value) return this;
        Object.keys(this.value).forEach((k) => {
            this.value[k.replace(/_(\w)/g, (all, letter) => letter.toUpperCase())] = this.value[k];
            if (isReplace && k.includes('_')) delete this.value[k];
        });
        return this;
    };

    // 抖动去除空值
    shake = (): Value => {
        if (!this.value) return this.value;
        return Object.entries(this.value).reduce((s: Record<string, any>, [k, v]) => {
            if (v) {
                if (typeof v === 'object' && !Array.isArray(v)) {
                    const value = new Obj(v).shake();
                    return value ? { ...s, [k]: value } : s;
                } else return { ...s, [k]: v };
            } else {
                if (typeof v === 'number') return { ...s, [k]: v };
                else return s;
            }
        }, null);
    };

    /**
     * 是否为json
     *
     * @description 判断是否为JSON对象或者字符串
     */
    isJson = (): boolean => {
        if (!this.value) return false;
        const exceptions = ['Symbol', 'Undefined', 'Null', 'Function'].map((item) => `[object ${item}]`);
        if (exceptions.includes(toString.call(this.value))) return false;

        try {
            if (typeof this.value === 'string') {
                if (typeof JSON.parse(this.value) !== 'object' || this.value === 'null') {
                    throw new Error('not a json string');
                }
            } else {
                JSON.stringify(this.value);
            }

            return true;
        } catch (err) {
            return false;
        }
    };
}
